/**
 * 프로젝트 대시보드 페이지
 * 프로젝트 그리드, 검색/정렬, 생성/삭제/복제/이름 변경
 */
'use client'

import { useEffect, useState, useCallback, useRef } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Plus,
  Film,
  MoreHorizontal,
  Clock,
  Search,
  SortAsc,
  SortDesc,
  Grid3X3,
  List,
  Trash2,
  Copy,
  Pencil,
  FolderOpen,
  Loader2,
  AlertCircle,
  X,
  Upload,
  FileImage,
  FileVideo,
  FileAudio,
  HardDrive,
  Image as ImageIcon,
  Cloud,
  CloudOff,
  RefreshCw,
  Check,
  Youtube,
  Video,
  Smartphone,
  Sparkles,
  ArrowRight,
} from 'lucide-react'
import V36TopNav from '@/components/layout/V36TopNav'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useProjectStore } from '@/store/projectStore'
import { useAuthStore } from '@/store/authStore'
import api from '@/lib/api'
import { syncManager } from '@/lib/syncManager'
import type { Media, ProjectType } from '@/types'

// 프로젝트 타입별 설정
const projectTypeConfig: Record<ProjectType, { label: string; color: string; bgColor: string; icon: React.ReactNode }> = {
  standard: { label: '편집기', color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30', icon: <Film className="h-3 w-3" /> },
  youtube_highlight: { label: 'YouTube', color: 'text-red-600', bgColor: 'bg-red-100 dark:bg-red-900/30', icon: <Youtube className="h-3 w-3" /> },
  longform_auto: { label: '롱폼', color: 'text-indigo-600', bgColor: 'bg-indigo-100 dark:bg-indigo-900/30', icon: <Video className="h-3 w-3" /> },
  shortform_auto: { label: '숏폼', color: 'text-emerald-600', bgColor: 'bg-emerald-100 dark:bg-emerald-900/30', icon: <Smartphone className="h-3 w-3" /> },
  wuxia_auto: { label: '무협', color: 'text-amber-600', bgColor: 'bg-amber-100 dark:bg-amber-900/30', icon: <Sparkles className="h-3 w-3" /> },
}

// 타입 필터 탭 목록
const typeFilterTabs: { value: ProjectType | 'all'; label: string }[] = [
  { value: 'all', label: '전체' },
  { value: 'standard', label: '편집기' },
  { value: 'youtube_highlight', label: 'YouTube 하이라이트' },
  { value: 'longform_auto', label: '롱폼' },
  { value: 'shortform_auto', label: '숏폼' },
  { value: 'wuxia_auto', label: '무협' },
]

// 새 프로젝트 생성 카드 목록
const creationModes = [
  {
    type: 'standard' as const,
    title: '동영상 편집기',
    description: '타임라인 기반 전문 편집',
    icon: <Film className="h-7 w-7" />,
    gradient: 'from-blue-500 to-indigo-600',
    href: null, // 프로젝트 생성 후 /editor/[id]
  },
  {
    type: 'youtube_highlight' as const,
    title: 'YouTube 하이라이트',
    description: 'YouTube URL → AI 숏폼 자동생성',
    icon: <Youtube className="h-7 w-7" />,
    gradient: 'from-red-500 to-rose-600',
    href: '/youtube-highlight',
  },
  {
    type: 'longform_auto' as const,
    title: '롱폼 자동 메이커',
    description: '주제 입력 → AI 영상 자동 제작',
    icon: <Video className="h-7 w-7" />,
    gradient: 'from-indigo-500 to-purple-600',
    href: '/longform',
  },
  {
    type: 'shortform_auto' as const,
    title: '숏폼 자동 메이커',
    description: '템플릿 → AI 숏폼 대량 생산',
    icon: <Smartphone className="h-7 w-7" />,
    gradient: 'from-emerald-500 to-teal-600',
    href: '/shortform',
  },
  {
    type: 'wuxia_auto' as const,
    title: '무협 스튜디오',
    description: '로그라인 → AI 웹소설 영상 자동 제작',
    icon: <Sparkles className="h-7 w-7" />,
    gradient: 'from-amber-500 to-orange-600',
    href: '/wuxia',
  },
]

/** 프로젝트 타입에 따른 라우트 반환 */
function getProjectRoute(projectId: string, type?: ProjectType): string {
  switch (type) {
    case 'youtube_highlight':
      return `/youtube-highlight/${projectId}`
    case 'longform_auto':
      return `/longform/${projectId}`
    case 'shortform_auto':
      return `/shortform/${projectId}`
    case 'wuxia_auto':
      return `/wuxia/${projectId}`
    default:
      return `/editor/${projectId}`
  }
}

// 시간 포맷 (상대 시간)
function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMin < 1) return '방금 전'
  if (diffMin < 60) return `${diffMin}분 전`
  if (diffHours < 24) return `${diffHours}시간 전`
  if (diffDays < 7) return `${diffDays}일 전`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}주 전`
  return date.toLocaleDateString('ko-KR')
}

// 초를 분:초로 변환
function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

export default function ProjectsPage() {
  const router = useRouter()
  const { isAuthenticated } = useAuthStore()
  const {
    projects,
    isLoading,
    error,
    searchQuery,
    sortBy,
    sortOrder,
    typeFilter,
    fetchProjects,
    createProject,
    deleteProject,
    duplicateProject,
    renameProject,
    setSearchQuery,
    setSortBy,
    setSortOrder,
    setTypeFilter,
    setError,
  } = useProjectStore()

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')
  const [contextMenu, setContextMenu] = useState<string | null>(null)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  // ── 클라우드 동기화 상태 ──
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'synced' | 'offline'>('idle')
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null)

  useEffect(() => {
    // 동기화 매니저 콜백 설정 및 시작
    syncManager.onSyncComplete = () => {
      setSyncStatus('synced')
      setLastSyncTime(syncManager.getLastSyncAt())
      fetchProjects() // 동기화 후 프로젝트 목록 갱신
    }
    syncManager.startSync(300000) // 5분 간격 자동 동기화

    // 온/오프라인 상태 감지
    const handleOnline = () => setSyncStatus('idle')
    const handleOffline = () => setSyncStatus('offline')
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    if (!navigator.onLine) setSyncStatus('offline')

    return () => {
      syncManager.stopSync()
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 수동 동기화 버튼 핸들러
  const handleManualSync = useCallback(async () => {
    setSyncStatus('syncing')
    try {
      await syncManager.syncNow()
      setSyncStatus('synced')
      setLastSyncTime(syncManager.getLastSyncAt())
    } catch {
      setSyncStatus('idle')
    }
  }, [])

  // 썸네일 자동 생성 — 캔버스가 존재할 때 toDataURL로 캡처
  const generateThumbnail = useCallback(async (projectId: string): Promise<string | null> => {
    try {
      const canvasEl = document.querySelector('canvas') as HTMLCanvasElement | null
      if (canvasEl) {
        return canvasEl.toDataURL('image/jpeg', 0.5)
      }
    } catch {
      // 캔버스 접근 실패 시 null
    }
    return null
  }, [])

  // 미디어 관리 상태
  const [mediaDialogProjectId, setMediaDialogProjectId] = useState<string | null>(null)
  const [mediaList, setMediaList] = useState<Media[]>([])
  const [mediaLoading, setMediaLoading] = useState(false)
  const [mediaUploading, setMediaUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 미디어 목록 로드
  const loadMedia = useCallback(async (projectId: string) => {
    setMediaLoading(true)
    try {
      const data = await api.get<{ items: Media[] }>(`/media?projectId=${projectId}`)
      setMediaList(data.items || [])
    } catch {
      setMediaList([])
    } finally {
      setMediaLoading(false)
    }
  }, [])

  // 미디어 관리 다이얼로그 열기
  const openMediaDialog = useCallback((projectId: string) => {
    setMediaDialogProjectId(projectId)
    setContextMenu(null)
    loadMedia(projectId)
  }, [loadMedia])

  // 미디어 삭제
  const handleDeleteMedia = useCallback(async (mediaId: string) => {
    if (!window.confirm('이 미디어를 삭제하시겠습니까?')) return
    try {
      await api.del(`/media/${mediaId}`)
      setMediaList((prev) => prev.filter((m) => m.id !== mediaId))
    } catch {
      // 삭제 실패 시 무시
    }
  }, [])

  // 미디어 업로드
  const handleMediaUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0 || !mediaDialogProjectId) return

    setMediaUploading(true)
    try {
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData()
        formData.append('file', files[i])
        formData.append('projectId', mediaDialogProjectId)
        await api.upload<Media>('/media/upload', formData)
      }
      // 업로드 후 목록 새로고침
      await loadMedia(mediaDialogProjectId)
    } catch {
      // 업로드 실패
    } finally {
      setMediaUploading(false)
      // input 초기화
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [mediaDialogProjectId, loadMedia])

  // 파일 크기 포맷
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
  }

  // 미디어 타입 아이콘
  const getMediaTypeIcon = (type: string) => {
    switch (type) {
      case 'video': return <FileVideo className="h-4 w-4" />
      case 'audio': return <FileAudio className="h-4 w-4" />
      case 'image': return <FileImage className="h-4 w-4" />
      default: return <HardDrive className="h-4 w-4" />
    }
  }

  // 미디어 타입 배지 색상
  const getMediaTypeBadgeVariant = (type: string): 'default' | 'secondary' | 'outline' | 'destructive' => {
    switch (type) {
      case 'video': return 'default'
      case 'audio': return 'secondary'
      case 'image': return 'outline'
      default: return 'secondary'
    }
  }

  // 프로젝트 목록 로드
  useEffect(() => {
    fetchProjects()
  }, [fetchProjects, sortBy, sortOrder, typeFilter])

  // 검색어 변경 시 디바운스 재조회
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProjects()
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery, fetchProjects])

  // 새 프로젝트 생성
  const handleCreateProject = useCallback(async () => {
    const name = newProjectName.trim() || '제목 없는 프로젝트'
    try {
      const projectId = await createProject(name)
      // 썸네일 자동 생성 시도
      const thumbnail = await generateThumbnail(projectId)
      if (thumbnail) {
        try {
          await api.patch(`/projects/${projectId}`, { thumbnailUrl: thumbnail })
        } catch {
          // 썸네일 저장 실패는 무시
        }
      }
      setShowCreateModal(false)
      setNewProjectName('')
      router.push(`/editor/${projectId}`)
    } catch {
      // 에러는 스토어에서 관리
    }
  }, [newProjectName, createProject, router, generateThumbnail])

  // 프로젝트 삭제
  const handleDelete = useCallback(async (projectId: string) => {
    await deleteProject(projectId)
    setDeleteConfirmId(null)
    setContextMenu(null)
  }, [deleteProject])

  // 프로젝트 복제
  const handleDuplicate = useCallback(async (projectId: string) => {
    await duplicateProject(projectId)
    setContextMenu(null)
  }, [duplicateProject])

  // 이름 변경 시작
  const startRename = useCallback((projectId: string, currentName: string) => {
    setRenamingId(projectId)
    setRenameValue(currentName)
    setContextMenu(null)
  }, [])

  // 이름 변경 완료
  const handleRename = useCallback(async () => {
    if (renamingId && renameValue.trim()) {
      await renameProject(renamingId, renameValue.trim())
    }
    setRenamingId(null)
    setRenameValue('')
  }, [renamingId, renameValue, renameProject])

  // 정렬 토글
  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')
  }

  return (
    <>
      <V36TopNav />
      <main className="min-h-screen bg-[var(--background)]">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
          {/* 페이지 헤더 */}
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold sm:text-3xl">내 프로젝트</h1>
              <p className="mt-1 text-sm text-[var(--muted-foreground)]">
                동영상 프로젝트를 관리하고 새로운 프로젝트를 시작하세요
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* 클라우드 동기화 상태 */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleManualSync}
                disabled={syncStatus === 'syncing' || syncStatus === 'offline'}
                className="gap-1.5 text-xs"
              >
                {syncStatus === 'offline' ? (
                  <><CloudOff className="h-3.5 w-3.5 text-red-500" /> 오프라인</>
                ) : syncStatus === 'syncing' ? (
                  <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> 동기화 중...</>
                ) : syncStatus === 'synced' ? (
                  <><Check className="h-3.5 w-3.5 text-green-500" /> 동기화 완료</>
                ) : (
                  <><Cloud className="h-3.5 w-3.5" /> 동기화</>
                )}
              </Button>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="gap-2 border-0 text-white hover:opacity-90 shadow-md"
              >
                <Plus className="h-4 w-4" />
                새 프로젝트
              </Button>
            </div>
          </div>

          {/* 검색 & 필터 바 */}
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {/* 검색 */}
            <div className="relative w-full sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--muted-foreground)]" />
              <Input
                placeholder="프로젝트 검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* 필터/뷰 옵션 */}
            <div className="flex items-center gap-2">
              {/* 정렬 기준 */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'updatedAt' | 'createdAt' | 'name')}
                className="h-10 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--ring)]"
              >
                <option value="updatedAt">최근 수정</option>
                <option value="createdAt">생성일</option>
                <option value="name">이름</option>
              </select>

              {/* 정렬 순서 토글 */}
              <Button variant="outline" size="icon" onClick={toggleSortOrder} title={sortOrder === 'desc' ? '내림차순' : '오름차순'}>
                {sortOrder === 'desc' ? <SortDesc className="h-4 w-4" /> : <SortAsc className="h-4 w-4" />}
              </Button>

              {/* 뷰 모드 토글 */}
              <div className="hidden sm:flex items-center rounded-lg border border-[var(--border)] p-0.5">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`rounded-md p-1.5 transition-colors ${
                    viewMode === 'grid' ? 'bg-[var(--accent)] text-[var(--foreground)]' : 'text-[var(--muted-foreground)]'
                  }`}
                >
                  <Grid3X3 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`rounded-md p-1.5 transition-colors ${
                    viewMode === 'list' ? 'bg-[var(--accent)] text-[var(--foreground)]' : 'text-[var(--muted-foreground)]'
                  }`}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* 타입 필터 탭 */}
          <div className="mb-6 flex items-center gap-1.5 overflow-x-auto pb-1">
            {typeFilterTabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setTypeFilter(tab.value)}
                className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  typeFilter === tab.value
                    ? 'bg-[var(--primary)] text-white shadow-sm'
                    : 'bg-[var(--muted)] text-[var(--muted-foreground)] hover:bg-[var(--accent)] hover:text-[var(--foreground)]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* 에러 메시지 */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 flex items-center gap-2 rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 p-4 text-sm text-red-600 dark:text-red-400"
            >
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span className="flex-1">{error}</span>
              <button onClick={() => setError(null)} className="shrink-0 hover:opacity-80">
                <X className="h-4 w-4" />
              </button>
            </motion.div>
          )}

          {/* 로딩 상태 */}
          {isLoading && projects.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)] mb-4" />
              <p className="text-sm text-[var(--muted-foreground)]">프로젝트를 불러오는 중...</p>
            </div>
          ) : projects.length === 0 && !isLoading ? (
            // 빈 상태
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="mb-4 rounded-2xl bg-[var(--muted)] p-6">
                <FolderOpen className="h-12 w-12 text-[var(--muted-foreground)]" />
              </div>
              <h3 className="text-lg font-semibold mb-2">프로젝트가 없습니다</h3>
              <p className="text-sm text-[var(--muted-foreground)] mb-6 max-w-md">
                {searchQuery
                  ? `"${searchQuery}" 검색 결과가 없습니다. 다른 검색어를 시도해보세요.`
                  : '새 프로젝트를 만들어 동영상 편집을 시작하세요.'}
              </p>
              {!searchQuery && (
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="gap-2 border-0 text-white hover:opacity-90"
                >
                  <Plus className="h-4 w-4" />
                  첫 프로젝트 만들기
                </Button>
              )}
            </div>
          ) : (
            // 프로젝트 그리드/리스트
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
              }}
              className={
                viewMode === 'grid'
                  ? 'grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                  : 'flex flex-col gap-3'
              }
            >
              {/* 새 프로젝트 카드 (그리드 모드) */}
              {viewMode === 'grid' && (
                <motion.div
                  variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                >
                  <Card
                    className="group cursor-pointer border-dashed border-2 hover:border-[var(--primary)] transition-all"
                    onClick={() => setShowCreateModal(true)}
                  >
                    <CardContent className="flex h-52 flex-col items-center justify-center gap-3 p-6">
                      <div className="rounded-full bg-[var(--muted)] p-4 group-hover:bg-[var(--primary)]/10 transition-colors">
                        <Plus className="h-7 w-7 text-[var(--muted-foreground)] group-hover:text-[var(--primary)]" />
                      </div>
                      <span className="text-sm font-semibold text-[var(--muted-foreground)] group-hover:text-[var(--primary)]">
                        새 프로젝트 만들기
                      </span>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* 프로젝트 카드 */}
              {projects.map((project) => (
                <motion.div
                  key={project.id}
                  variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                  className={`relative ${contextMenu === project.id ? 'z-50' : ''}`}
                >
                  {viewMode === 'grid' ? (
                    // 그리드 뷰 카드
                    <Link href={`/editor/${project.id}`}>
                      <Card className="group cursor-pointer hover:ring-2 hover:ring-[var(--primary)]/50 transition-all">
                        {/* 썸네일 */}
                        <div className="relative h-40 bg-gradient-to-br from-[var(--muted)] to-[var(--accent)] flex items-center justify-center overflow-hidden">
                          {project.thumbnailUrl ? (
                            <img
                              src={project.thumbnailUrl}
                              alt={project.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <Film className="h-12 w-12 text-[var(--muted-foreground)]/50" />
                          )}
                          {/* 재생 시간 배지 */}
                          <div className="absolute bottom-2 right-2 rounded-md bg-black/70 px-2 py-0.5 text-xs font-medium text-white backdrop-blur-sm">
                            {formatDuration(project.duration)}
                          </div>
                          {/* 호버 오버레이 */}
                          <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors">
                            <div className="rounded-full bg-white/90 p-2.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                              <Film className="h-5 w-5 text-[var(--primary)]" />
                            </div>
                          </div>
                        </div>

                        {/* 정보 */}
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              {renamingId === project.id ? (
                                <input
                                  autoFocus
                                  value={renameValue}
                                  onChange={(e) => setRenameValue(e.target.value)}
                                  onBlur={handleRename}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleRename()
                                    if (e.key === 'Escape') { setRenamingId(null); setRenameValue('') }
                                  }}
                                  onClick={(e) => e.preventDefault()}
                                  className="w-full rounded border border-[var(--primary)] bg-transparent px-1.5 py-0.5 text-sm font-medium outline-none"
                                />
                              ) : (
                                <h3 className="truncate text-sm font-semibold">{project.name}</h3>
                              )}
                              <div className="mt-1.5 flex items-center gap-1.5 text-xs text-[var(--muted-foreground)]">
                                <Clock className="h-3 w-3" />
                                {formatRelativeTime(project.updatedAt)}
                              </div>
                            </div>

                            {/* 컨텍스트 메뉴 버튼 — 항상 표시 */}
                            <div className="relative shrink-0">
                              <button
                                className="rounded-md p-1.5 hover:bg-[var(--muted)] transition-colors"
                                onClick={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  setContextMenu(contextMenu === project.id ? null : project.id)
                                }}
                              >
                                <MoreHorizontal className="h-4 w-4 text-[var(--muted-foreground)]" />
                              </button>

                              {/* 드롭다운 메뉴 */}
                              <AnimatePresence>
                                {contextMenu === project.id && (
                                  <>
                                    <div
                                      className="fixed inset-0 z-40"
                                      onClick={(e) => { e.preventDefault(); setContextMenu(null) }}
                                    />
                                    <motion.div
                                      initial={{ opacity: 0, scale: 0.95 }}
                                      animate={{ opacity: 1, scale: 1 }}
                                      exit={{ opacity: 0, scale: 0.95 }}
                                      transition={{ duration: 0.1 }}
                                      className="absolute right-0 top-full z-50 mt-1 w-44 rounded-xl border border-[var(--border)] bg-[var(--popover)] p-1 shadow-lg"
                                      onClick={(e) => e.preventDefault()}
                                    >
                                      <button
                                        onClick={(e) => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                          startRename(project.id, project.name)
                                        }}
                                        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-[var(--accent)] transition-colors"
                                      >
                                        <Pencil className="h-3.5 w-3.5 text-[var(--muted-foreground)]" />
                                        이름 변경
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                          handleDuplicate(project.id)
                                        }}
                                        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-[var(--accent)] transition-colors"
                                      >
                                        <Copy className="h-3.5 w-3.5 text-[var(--muted-foreground)]" />
                                        복제
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                          openMediaDialog(project.id)
                                        }}
                                        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-[var(--accent)] transition-colors"
                                      >
                                        <ImageIcon className="h-3.5 w-3.5 text-[var(--muted-foreground)]" />
                                        미디어 관리
                                      </button>
                                      <div className="my-1 h-px bg-[var(--border)]" />
                                      <button
                                        onClick={(e) => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                          setDeleteConfirmId(project.id)
                                          setContextMenu(null)
                                        }}
                                        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-[var(--destructive)] hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                        삭제
                                      </button>
                                    </motion.div>
                                  </>
                                )}
                              </AnimatePresence>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ) : (
                    // 리스트 뷰
                    <Link href={`/editor/${project.id}`}>
                      <Card className="group cursor-pointer hover:ring-2 hover:ring-[var(--primary)]/50 transition-all">
                        <CardContent className="flex items-center gap-4 p-4">
                          {/* 썸네일 */}
                          <div className="flex h-16 w-24 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[var(--muted)] to-[var(--accent)] overflow-hidden">
                            {project.thumbnailUrl ? (
                              <img
                                src={project.thumbnailUrl}
                                alt={project.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <Film className="h-6 w-6 text-[var(--muted-foreground)]/50" />
                            )}
                          </div>

                          {/* 정보 */}
                          <div className="min-w-0 flex-1">
                            <h3 className="truncate text-sm font-semibold">{project.name}</h3>
                            <div className="mt-1 flex items-center gap-4 text-xs text-[var(--muted-foreground)]">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatRelativeTime(project.updatedAt)}
                              </span>
                              <span>{formatDuration(project.duration)}</span>
                            </div>
                          </div>

                          {/* 액션 */}
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => { e.preventDefault(); handleDuplicate(project.id) }}
                              title="복제"
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-[var(--destructive)]"
                              onClick={(e) => { e.preventDefault(); setDeleteConfirmId(project.id) }}
                              title="삭제"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  )}
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </main>

      {/* ==================== 새 프로젝트 생성 모달 ==================== */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-md rounded-2xl border border-[var(--border)] bg-[var(--card)] p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-lg font-bold mb-1">새 프로젝트</h2>
              <p className="text-sm text-[var(--muted-foreground)] mb-6">
                프로젝트 이름을 입력하세요
              </p>

              <Input
                autoFocus
                placeholder="프로젝트 이름 (예: 나의 첫 영상)"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateProject() }}
                className="mb-6"
              />

              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => { setShowCreateModal(false); setNewProjectName('') }}
                >
                  취소
                </Button>
                <Button
                  onClick={handleCreateProject}
                  disabled={isLoading}
                  className="border-0 text-white hover:opacity-90"
                >
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  프로젝트 만들기
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ==================== 삭제 확인 모달 ==================== */}
      <AnimatePresence>
        {deleteConfirmId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            onClick={() => setDeleteConfirmId(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-sm rounded-2xl border border-[var(--border)] bg-[var(--card)] p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-950/30">
                <Trash2 className="h-6 w-6 text-[var(--destructive)]" />
              </div>
              <h2 className="text-lg font-bold mb-1">프로젝트 삭제</h2>
              <p className="text-sm text-[var(--muted-foreground)] mb-6">
                이 프로젝트를 삭제하시겠습니까? 삭제된 프로젝트는 복구할 수 없습니다.
              </p>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
                  취소
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDelete(deleteConfirmId)}
                >
                  삭제
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ==================== 미디어 관리 다이얼로그 ==================== */}
      <AnimatePresence>
        {mediaDialogProjectId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            onClick={() => setMediaDialogProjectId(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-2xl max-h-[80vh] rounded-2xl border border-[var(--border)] bg-[var(--card)] shadow-2xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 헤더 */}
              <div className="flex items-center justify-between p-6 pb-4 border-b border-[var(--border)]">
                <div>
                  <h2 className="text-lg font-bold">미디어 관리</h2>
                  <p className="text-sm text-[var(--muted-foreground)] mt-0.5">
                    프로젝트에 포함된 미디어 파일을 관리합니다
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {/* 업로드 버튼 */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="video/*,audio/*,image/*"
                    className="hidden"
                    onChange={handleMediaUpload}
                  />
                  <Button
                    size="sm"
                    className="gap-1.5 border-0 text-white hover:opacity-90"
                    disabled={mediaUploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {mediaUploading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Upload className="h-3.5 w-3.5" />
                    )}
                    {mediaUploading ? '업로드 중...' : '파일 업로드'}
                  </Button>
                  <button
                    onClick={() => setMediaDialogProjectId(null)}
                    className="rounded-md p-1.5 hover:bg-[var(--muted)] transition-colors"
                  >
                    <X className="h-4 w-4 text-[var(--muted-foreground)]" />
                  </button>
                </div>
              </div>

              {/* 미디어 목록 */}
              <div className="flex-1 overflow-y-auto p-6 pt-4">
                {mediaLoading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)] mb-3" />
                    <p className="text-sm text-[var(--muted-foreground)]">미디어를 불러오는 중...</p>
                  </div>
                ) : mediaList.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="mb-3 rounded-2xl bg-[var(--muted)] p-4">
                      <ImageIcon className="h-8 w-8 text-[var(--muted-foreground)]" />
                    </div>
                    <p className="text-sm font-medium mb-1">미디어가 없습니다</p>
                    <p className="text-xs text-[var(--muted-foreground)]">
                      파일을 업로드하여 프로젝트에 미디어를 추가하세요
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {mediaList.map((media) => (
                      <div
                        key={media.id}
                        className="group flex items-center gap-3 rounded-xl border border-[var(--border)] bg-[var(--background)] p-3 hover:border-[var(--primary)]/30 transition-colors"
                      >
                        {/* 썸네일 */}
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-[var(--muted)] overflow-hidden">
                          {media.thumbnailUrl ? (
                            <img
                              src={media.thumbnailUrl}
                              alt={media.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <span className="text-[var(--muted-foreground)]">
                              {getMediaTypeIcon(media.type)}
                            </span>
                          )}
                        </div>

                        {/* 정보 */}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate" title={media.name}>
                            {media.name}
                          </p>
                          <div className="mt-1 flex items-center gap-2">
                            <Badge variant={getMediaTypeBadgeVariant(media.type)} className="text-[10px] px-1.5 py-0">
                              {media.type === 'video' ? '영상' : media.type === 'audio' ? '오디오' : '이미지'}
                            </Badge>
                            <span className="text-[11px] text-[var(--muted-foreground)]">
                              {formatFileSize(media.fileSize)}
                            </span>
                            {media.width && media.height && (
                              <span className="text-[11px] text-[var(--muted-foreground)]">
                                {media.width}x{media.height}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* 삭제 버튼 */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0 text-[var(--muted-foreground)] opacity-0 group-hover:opacity-100 hover:text-[var(--destructive)] transition-all"
                          title="삭제"
                          onClick={() => handleDeleteMedia(media.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 푸터 — 미디어 개수 */}
              {!mediaLoading && mediaList.length > 0 && (
                <div className="px-6 py-3 border-t border-[var(--border)] text-xs text-[var(--muted-foreground)]">
                  총 {mediaList.length}개 미디어
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
